import { isFinishedAnalysisStatus } from '../../shared/analysis-status.js';
import type { AnalysisReport, QuoteMatrix } from '../data/api.js';

export type PricedDecisionAction = 'approve' | 'negotiate';

export interface RecommendedBatchDecision {
  lineId: string;
  chosenQuoteLineId: string;
  price: number;
}

function positiveNumber(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** True when the displayed, completed pricing check recommends an action for the
 * line, so "Accept AI Recommendation" can apply it with that version as the
 * decision's source (HS2-A0851K). */
export function hasApplicableRecommendation(lineId: string, report: AnalysisReport | null): boolean {
  if (report === null || !isFinishedAnalysisStatus(report.analysis.status)) return false;
  const verdict = report.verdicts.find((candidate) => candidate.lineItemId === lineId);
  return verdict?.recommendedAction !== null && verdict?.recommendedAction !== undefined;
}

/** Resolve the analysis's recommended vendor and action-specific price for every
 * selected line. Undefined means the action must be disabled for the batch. */
export function recommendedBatchDecisions(
  action: PricedDecisionAction,
  lineIds: string[],
  matrix: QuoteMatrix | null,
  report: AnalysisReport | null,
): RecommendedBatchDecision[] | undefined {
  if (lineIds.length === 0 || matrix === null || report === null) return undefined;
  const decisions: RecommendedBatchDecision[] = [];
  for (const lineId of lineIds) {
    const verdict = report.verdicts.find((candidate) => candidate.lineItemId === lineId);
    const vendorId = verdict?.bestQuoteVendorId;
    const vendor =
      vendorId === null || vendorId === undefined
        ? undefined
        : matrix.columns.find((candidate) => candidate.vendorId === vendorId);
    const cell = vendor === undefined ? undefined : matrix.cells[lineId]?.[vendor.quotationId];
    const price = action === 'approve' ? positiveNumber(verdict?.bestQuotePrice) : positiveNumber(verdict?.targetPrice);
    if (cell === undefined || cell.basePrice === null || price === undefined) return undefined;
    decisions.push({ lineId, chosenQuoteLineId: cell.quoteLineId, price });
  }
  return decisions;
}
